/* eslint-disable react-refresh/only-export-components -- bewusst co-lozierter Provider+Hook bzw. Helfer-Export; react-refresh ist eine reine Dev-HMR-Optimierung, kein Prod-/Qualitaetsthema. */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@/lib/auth-context';

interface LegalToggleApi {
  showLegalHints: boolean;
  toggleLegalHints: () => void;
  setShowLegalHints: (value: boolean) => void;
}

const LegalToggleContext = createContext<LegalToggleApi | null>(null);

const STORAGE_KEY = 'ignisai.legal-hints';

function readInitial(): boolean | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    return raw === 'true';
  } catch {
    return null;
  }
}

export function LegalToggleProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [showLegalHints, setShowLegalHintsState] = useState<boolean>(() => {
    const stored = readInitial();
    if (stored !== null) return stored;
    return true; // Default: on
  });

  // User-Setting sync (preferences vom Backend)
  useEffect(() => {
    if (!user) return;
    const stored = readInitial();
    if (stored === null) {
      setShowLegalHintsState(user.showLegalHints);
    }
  }, [user]);

  const setShowLegalHints = useCallback((value: boolean) => {
    setShowLegalHintsState(value);
    try {
      localStorage.setItem(STORAGE_KEY, String(value));
    } catch {
      /* ignore */
    }
  }, []);

  const toggleLegalHints = useCallback(() => {
    setShowLegalHints(!showLegalHints);
  }, [showLegalHints, setShowLegalHints]);

  // Strg+J Shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        toggleLegalHints();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggleLegalHints]);

  const value = useMemo<LegalToggleApi>(
    () => ({ showLegalHints, toggleLegalHints, setShowLegalHints }),
    [showLegalHints, toggleLegalHints, setShowLegalHints],
  );

  return <LegalToggleContext.Provider value={value}>{children}</LegalToggleContext.Provider>;
}

export function useLegalToggle(): LegalToggleApi {
  const ctx = useContext(LegalToggleContext);
  if (!ctx) throw new Error('useLegalToggle must be used inside <LegalToggleProvider>');
  return ctx;
}
